import type { WidgetType } from '../../../api/dashboards.js';

interface Props {
  onAdd(type: WidgetType): void;
  disabled?: boolean;
}

/**
 * Left-rail palette of widget types. Clicking adds a new widget to the
 * canvas at the first available position. (We also support HTML5 drag,
 * but react-grid-layout's drop handling is heavy-weight; for now the
 * click-to-add workflow is simpler and more enterprise-reliable.)
 */
export function WidgetPalette({ onAdd, disabled }: Props): JSX.Element {
  return (
    <aside className="widget-palette">
      <h3>Widgets</h3>
      <p className="widget-palette-hint">Click to add to the canvas.</p>
      <div className="widget-palette-grid">
        {PALETTE_ITEMS.map((item) => (
          <button
            key={item.type}
            type="button"
            className="widget-palette-item"
            onClick={() => onAdd(item.type)}
            disabled={disabled}
            title={item.description}
          >
            <div className="widget-palette-icon" aria-hidden="true">
              {item.icon}
            </div>
            <div className="widget-palette-label">{item.label}</div>
          </button>
        ))}
      </div>
    </aside>
  );
}

const PALETTE_ITEMS: Array<{
  type: WidgetType;
  label: string;
  description: string;
  icon: JSX.Element;
}> = [
  {
    type: 'kpi',
    label: 'KPI',
    description: 'Big-number tile with optional delta vs. prior period.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <text
          x="12"
          y="15"
          textAnchor="middle"
          fontSize="10"
          fontWeight="700"
          fill="currentColor"
          stroke="none"
        >
          42
        </text>
        <rect x="3" y="4" width="18" height="16" rx="2" />
      </svg>
    ),
  },
  {
    type: 'bar',
    label: 'Bar',
    description: 'Categorical bar chart — one bar per x value.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      >
        <rect x="4" y="12" width="3" height="8" />
        <rect x="10" y="8" width="3" height="12" />
        <rect x="16" y="4" width="3" height="16" />
      </svg>
    ),
  },
  {
    type: 'line',
    label: 'Line',
    description: 'Time-series or ordered line chart.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="3,18 8,12 13,15 18,6 21,9" />
      </svg>
    ),
  },
  {
    type: 'area',
    label: 'Area',
    description: 'Filled line chart; good for totals over time.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      >
        <path
          d="M3 18 L8 12 L13 15 L18 6 L21 9 L21 20 L3 20 Z"
          fill="currentColor"
          fillOpacity="0.25"
        />
      </svg>
    ),
  },
  {
    type: 'pie',
    label: 'Pie',
    description: 'Category share — one slice per category.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      >
        <circle cx="12" cy="12" r="8" />
        <path d="M12 4 L12 12 L20 12 A8 8 0 0 0 12 4 Z" fill="currentColor" />
      </svg>
    ),
  },
  {
    type: 'donut',
    label: 'Donut',
    description: 'Pie with a center hole; same data, different emphasis.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      >
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    type: 'table',
    label: 'Table',
    description: 'Sortable tabular grid with per-column formatting.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      >
        <rect x="3" y="4" width="18" height="16" rx="1" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="3" y1="14" x2="21" y2="14" />
        <line x1="9" y1="4" x2="9" y2="20" />
        <line x1="15" y1="4" x2="15" y2="20" />
      </svg>
    ),
  },
  {
    type: 'text',
    label: 'Text',
    description: 'Narrative block with markdown support.',
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      >
        <line x1="4" y1="7" x2="20" y2="7" />
        <line x1="4" y1="12" x2="20" y2="12" />
        <line x1="4" y1="17" x2="14" y2="17" />
      </svg>
    ),
  },
];
